// Copies `fn.*` extensions from the host page's jQuery (snapshotted by
// `_capture_host_jquery.js`) onto Sparkle's bundled jQuery so plugins
// attached by the host (e.g. atriumhealth.org's `fullCalendar`) remain
// callable after `window.jQuery` has been overwritten.
//
// Also installs a setter trap on `window.jQuery` so that if the host CMS
// loads its own jQuery AFTER Sparkle's bundle (e.g. Floyd's Next.js app),
// Sparkle's plugins (`uberSelect`, `slick`, etc.) are automatically
// forwarded to the new instance.
//
// Only fills in missing keys — each side's own `fn` entries always win.
//
// The locale ajax prefilter is installed on the new instance too, so requests it makes keep the page locale.
import { installLocaleAjaxPrefilter } from "./locale-ajax-prefilter.js";

const host = typeof window !== "undefined" ? window.__sparkleCapturedHostJQuery : null;
if (host && window.jQuery && host !== window.jQuery) {
  for (const key of Object.keys(host.fn || {})) {
    if (window.jQuery.fn[key] === undefined) {
      window.jQuery.fn[key] = host.fn[key];
    }
  }
}
delete window.__sparkleCapturedHostJQuery;

// Trap future overwrites of window.jQuery / window.$ so Sparkle plugins
// survive host scripts that load jQuery after Sparkle's bundle.
if (typeof window !== "undefined" && window.jQuery) {
  const sparklePluginKeys = Object.keys(window.jQuery.fn).filter((k) => !["constructor", "jquery", "init"].includes(k));
  const sparkleFn = window.jQuery.fn;
  let currentJQuery = window.jQuery;

  function forwardPlugins(newJQ) {
    if (!newJQ || !newJQ.fn || newJQ === currentJQuery) {
      return;
    }
    // Copy Sparkle plugins onto the new jQuery instance
    for (const key of sparklePluginKeys) {
      if (newJQ.fn[key] === undefined && sparkleFn[key]) {
        newJQ.fn[key] = sparkleFn[key];
      }
    }
    // Copy new jQuery's plugins back to Sparkle (host plugins)
    for (const key of Object.keys(newJQ.fn)) {
      if (sparkleFn[key] === undefined) {
        sparkleFn[key] = newJQ.fn[key];
      }
    }
    installLocaleAjaxPrefilter(newJQ);
    currentJQuery = newJQ;
  }

  try {
    Object.defineProperty(window, "jQuery", {
      get() {
        return currentJQuery;
      },
      set(val) {
        forwardPlugins(val);
        currentJQuery = val;
      },
      configurable: true,
    });
    // Also trap `$` which many host scripts use
    Object.defineProperty(window, "$", {
      get() {
        return currentJQuery;
      },
      set(val) {
        forwardPlugins(val);
        currentJQuery = val;
      },
      configurable: true,
    });
  } catch {
    // defineProperty can fail in strict mode or if already defined as
    // non-configurable. Fall back gracefully — the one-shot copy above
    // still handles the "host loads first" case.
  }
}
